/**
 * One Euro Filter (Casiez, Roussel, Vogel — CHI 2012).
 *
 * A first-order low-pass filter whose cutoff frequency adapts to the speed of
 * the signal: at rest the cutoff is `minCutoff` (strong smoothing, no jitter),
 * in motion the cutoff grows with `beta * |speed|` (low latency).
 *
 * Tuning procedure (from the paper, exposed in the debug panel):
 *   1. Set beta = 0 and tune minCutoff until jitter at rest is gone.
 *   2. Increase beta until lag during fast movements is acceptable.
 *   dCutoff (derivative cutoff) is almost never touched: 1 Hz is fine.
 */
export interface OneEuroParams {
  /** Minimum cutoff frequency in Hz. Lower = smoother at rest, more lag. */
  minCutoff: number;
  /** Speed coefficient. Higher = less lag in motion, more jitter in motion. */
  beta: number;
  /** Cutoff frequency (Hz) of the derivative estimator. */
  dCutoff: number;
}

export const DEFAULT_ONE_EURO: OneEuroParams = { minCutoff: 1.0, beta: 0.0, dCutoff: 1.0 };

/** Smoothing factor for a first-order low-pass filter at a given cutoff/dt. */
export function smoothingAlpha(cutoffHz: number, dtSeconds: number): number {
  const tau = 1 / (2 * Math.PI * Math.max(cutoffHz, 1e-6));
  return 1 / (1 + tau / Math.max(dtSeconds, 1e-6));
}

/** Exponential low-pass filter with an externally supplied alpha. */
export class LowPassFilter {
  private hatX: number | null = null;

  filter(x: number, alpha: number): number {
    if (this.hatX === null) {
      this.hatX = x;
    } else {
      this.hatX = alpha * x + (1 - alpha) * this.hatX;
    }
    return this.hatX;
  }

  get value(): number | null {
    return this.hatX;
  }

  reset(): void {
    this.hatX = null;
  }
}

export class OneEuroFilter {
  private params: OneEuroParams;
  private readonly xFilter = new LowPassFilter();
  private readonly dxFilter = new LowPassFilter();
  private lastRaw: number | null = null;
  private lastTime: number | null = null;
  private lastDerivative = 0;
  private lastCutoff = 0;

  constructor(params: Partial<OneEuroParams> = {}) {
    this.params = { ...DEFAULT_ONE_EURO, ...params };
  }

  setParams(params: Partial<OneEuroParams>): void {
    this.params = { ...this.params, ...params };
  }

  getParams(): OneEuroParams {
    return { ...this.params };
  }

  /**
   * @param x raw sample
   * @param tSeconds timestamp in seconds (monotonic)
   * @returns filtered value
   */
  filter(x: number, tSeconds: number): number {
    let dt = this.lastTime === null ? 1 / 60 : tSeconds - this.lastTime;
    if (!(dt > 0)) dt = 1 / 60; // duplicated / non-monotonic timestamps: assume one frame
    this.lastTime = tSeconds;

    const dx = this.lastRaw === null ? 0 : (x - this.lastRaw) / dt;
    this.lastRaw = x;

    const edx = this.dxFilter.filter(dx, smoothingAlpha(this.params.dCutoff, dt));
    this.lastDerivative = edx;

    const cutoff = this.params.minCutoff + this.params.beta * Math.abs(edx);
    this.lastCutoff = cutoff;
    return this.xFilter.filter(x, smoothingAlpha(cutoff, dt));
  }

  /** Filtered derivative (units/second) — used for short-horizon prediction. */
  get derivative(): number {
    return this.lastDerivative;
  }

  get currentCutoff(): number {
    return this.lastCutoff;
  }

  get value(): number | null {
    return this.xFilter.value;
  }

  reset(): void {
    this.xFilter.reset();
    this.dxFilter.reset();
    this.lastRaw = null;
    this.lastTime = null;
    this.lastDerivative = 0;
    this.lastCutoff = 0;
  }
}
